import { formatDateRu } from '@pms/shared';
import { useLiveQuery } from 'dexie-react-hooks';
import { Link } from 'react-router-dom';

import { db } from '../sync/db.js';
import type { OutboxRecord } from '../sync/index.js';

/**
 * Мои отчёты: что сдано, что ждёт отправки, что с фотографиями.
 *
 * Берётся из локальной базы, поэтому экран работает и без сети —
 * оператор всегда может убедиться, что смена сдана.
 */

const STATUS_LABEL: Record<string, string> = {
  pending: 'Ждёт отправки',
  sending: 'Отправляется',
  synced: 'Сдан',
  rejected: 'Отклонён',
};

const STATUS_TONE: Record<string, string> = {
  pending: 'bg-amber-500 text-black',
  sending: 'bg-sky-600 text-white',
  synced: 'bg-emerald-700 text-white',
  rejected: 'bg-red-600 text-white',
};

interface ReportRow {
  record: OutboxRecord;
  date: string;
  shiftNumber: number;
  siteName: string;
  machineName: string;
  photoStatus: string;
}

export function MyReportsScreen(): JSX.Element {
  const rows = useLiveQuery(
    async (): Promise<ReportRow[]> => {
      const records = await db.outbox.orderBy('createdAt').reverse().limit(100).toArray();
      const reports = records.filter((record) => record.event.type === 'SHIFT_REPORT_SUBMITTED');

      const result: ReportRow[] = [];
      for (const record of reports) {
        const payload = record.event.payload as {
          siteId: string;
          machineId: string;
          date: string;
          shiftNumber: number;
          reportId: string;
        };
        const [site, machine, photos] = await Promise.all([
          db.sites.get(payload.siteId),
          db.machines.get(payload.machineId),
          db.photos.where('reportId').equals(payload.reportId).toArray(),
        ]);

        result.push({
          record,
          date: payload.date,
          shiftNumber: payload.shiftNumber,
          siteName: site?.name ?? 'участок',
          machineName: machine?.name ?? 'агрегат',
          photoStatus:
            photos.length === 0
              ? 'не прикреплено'
              : photos.every((photo) => photo.status === 'uploaded')
                ? 'загружено'
                : 'ожидает загрузки',
        });
      }
      return result;
    },
    [],
    [] as ReportRow[],
  );

  const drafts = useLiveQuery(() => db.drafts.orderBy('updatedAt').reverse().toArray(), [], []);

  return (
    <div className="min-h-screen px-4 py-6">
      <header className="mb-6 flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Мои отчёты</h1>
        <Link to="/" className="rounded-xl bg-muted px-4 py-3 text-base font-semibold">
          Назад
        </Link>
      </header>

      {drafts.length > 0 && (
        <section className="mb-6">
          <h2 className="mb-3 text-lg font-semibold">Незаконченные</h2>
          <ul className="flex flex-col gap-touch">
            {drafts.map((draft) => (
              <li key={draft.id}>
                <Link
                  to={`/report/${draft.id}`}
                  className="block rounded-xl bg-amber-500 px-5 py-4 text-lg font-semibold text-black"
                >
                  Смена {draft.shiftNumber} за {formatDateRu(draft.date)} — продолжить
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {rows.length === 0 ? (
        <p className="rounded-xl bg-card shadow-card p-4 text-base text-dim">Отчётов пока нет</p>
      ) : (
        <ul className="flex flex-col gap-touch">
          {rows.map((row) => (
            <li key={row.record.id} className="rounded-xl bg-card shadow-card p-4">
              <div className="flex flex-wrap items-center justify-between gap-touch">
                <span
                  className={`rounded-lg px-3 py-1 text-base font-semibold ${
                    STATUS_TONE[row.record.status] ?? 'bg-muted'
                  }`}
                >
                  {STATUS_LABEL[row.record.status] ?? row.record.status}
                </span>
                <span className="text-base text-dim">
                  {formatDateRu(row.date)} · смена {row.shiftNumber}
                </span>
              </div>
              <p className="mt-2 text-base text-body">
                {row.siteName} · {row.machineName}
              </p>
              <p className="mt-1 text-base text-dim">Фото: {row.photoStatus}</p>
              {row.record.rejectionReason && (
                <p className="mt-2 text-base text-red-300">{row.record.rejectionReason}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
